import { snakeCase } from 'es-toolkit';

import isDefined from '../composables/useValidation/utils/isDefined';

interface Options {
  /**
   * A function that determines whether a param should be included in the query string based on its value.
   */
  shouldInclude?: (value: unknown, name: string) => boolean;
}

/**
 * Creates a URL query string from an object of key/value pairs.
 *
 * Values that are `undefined` will be excluded from the query string.
 *
 * For a param that has multiple values, use an array:
 *  - input: `{ brands: [1, 2, 3] }`
 *  - output: `brands=1&brands=2&brands=3`
 */
export function createQueryString(
  params: Record<string, unknown>,
  { shouldInclude = isDefined }: Options = {},
): string {
  const searchParams = new URLSearchParams({});

  Object.entries(params).forEach(([name, value]) => {
    if (!shouldInclude(value, name)) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item: string | number) => {
        searchParams.append(name, String(item));
      });
    } else {
      if (typeof value === 'object' && value !== null) {
        throw new TypeError(
          `Invalid query string parameter value.\n\nReceived value: "${value}".\n\nValues must be Primitive Values or arrays of Primitive Values. See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Data_structures#primitive_values`,
        );
      }

      searchParams.append(snakeCase(name), String(value));
    }
  });

  return searchParams.toString();
}

export default createQueryString;
